import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { CookieConsent } from './CookieConsent';
import { ThemeToggle } from './ui/ThemeToggle';
import { AdBanner } from './ads/AdBanner';
import { ADS_ENABLED } from '../config/ads';

const getTranslation = (t: (key: string) => string, key: string, fallback: string): string => {
  const translation = t(key);
  return translation === key ? fallback : translation;
};

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ka', name: 'ქართული', flag: '🇬🇪' },
];

function LanguageDropdown() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLanguage =
    languages.find((lang) => i18n.language?.startsWith(lang.code)) || languages[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 rounded-stitch-md border border-stitch-border bg-stitch-surface-2 px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-border/40 dark:hover:bg-stitch-surface-2"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="hidden md:inline">{currentLanguage.name}</span>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-44 rounded-stitch-lg border border-stitch-border bg-stitch-surface py-2 shadow-stitch-pop">
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => {
                void i18n.changeLanguage(language.code);
                setIsOpen(false);
              }}
              className={`flex w-full items-center px-4 py-2 text-left text-sm ${
                i18n.language?.startsWith(language.code)
                  ? 'bg-stitch-primary-soft text-stitch-primary dark:text-stitch-primary'
                  : 'text-stitch-fg-soft hover:bg-stitch-surface-2'
              }`}
            >
              <span className="mr-2 text-lg">{language.flag}</span>
              <span>{language.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, userRole, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/about');
    } else {
      navigate('/');
    }
  };

  const bookingsPath = '/bookings';

  return (
    <header className="sticky top-0 z-50 border-b border-stitch-border bg-stitch-surface/95 shadow-stitch-card backdrop-blur-md dark:bg-stitch-surface/90">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2">
            <span className="bg-gradient-to-r from-stitch-primary to-stitch-accent bg-clip-text text-2xl font-extrabold text-transparent sm:text-3xl">
              InterviewR
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <div className="mr-4 flex items-center gap-2">
              <ThemeToggle />
              <LanguageDropdown />
            </div>

            {!isAuthenticated ? (
              <>
                <Link
                  to="/become-interviewer"
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2 hover:text-stitch-fg"
                >
                  {getTranslation(t, 'nav.becomeInterviewer', 'Become an Interviewer')}
                </Link>
                <Link
                  to="/try-interview"
                  className="rounded-stitch-lg border border-stitch-accent/40 bg-stitch-accent-soft px-3 py-2 text-sm font-semibold text-stitch-accent hover:bg-stitch-accent/20 dark:text-stitch-accent"
                >
                  {getTranslation(t, 'nav.tryAi', 'Try AI interview')}
                </Link>
                <Link
                  to="/login"
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2 hover:text-stitch-fg"
                >
                  {getTranslation(t, 'auth.login', 'Login')}
                </Link>
                <Link
                  to="/register"
                  className="rounded-stitch-md bg-stitch-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-stitch-primary-hover"
                >
                  {getTranslation(t, 'auth.register', 'Sign Up')}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/about"
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                >
                  {getTranslation(t, 'nav.about', 'About')}
                </Link>
                {userRole === UserRole.Interviewer && (
                  <>
                    <Link
                      to={bookingsPath}
                      className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                    >
                      {getTranslation(t, 'bookings.interviewerBookings', 'My Bookings')}
                    </Link>
                    <Link
                      to="/time-slots"
                      className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                    >
                      {getTranslation(t, 'timeSlots.manage', 'Manage Slots')}
                    </Link>
                  </>
                )}
                {userRole !== UserRole.Interviewer && (
                  <>
                    <Link
                      to="/interviewers"
                      className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                    >
                      {getTranslation(t, 'interviewers.title', 'Find Interviewers')}
                    </Link>
                    <Link
                      to={bookingsPath}
                      className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                    >
                      {getTranslation(t, 'bookings.myBookings', 'My Bookings')}
                    </Link>
                    <Link
                      to="/become-interviewer"
                      className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                    >
                      {getTranslation(t, 'nav.becomeInterviewer', 'Become an Interviewer')}
                    </Link>
                  </>
                )}
                <Link
                  to="/interview-training"
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                >
                  {getTranslation(t, 'nav.aiTraining', 'AI Training')}
                </Link>
                <Link
                  to="/profile"
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                >
                  {getTranslation(t, 'profile.title', 'Profile')}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
                >
                  {getTranslation(t, 'auth.logout', 'Logout')}
                </button>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <LanguageDropdown />
            <button
              type="button"
              className="rounded-stitch-md p-2 text-stitch-fg-soft hover:bg-stitch-surface-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-stitch-border pb-3 md:hidden">
            <div className="space-y-1 px-2 pt-2">
              {!isAuthenticated ? (
                <>
                  <Link
                    to="/try-interview"
                    className="block rounded-stitch-md bg-stitch-accent-soft px-3 py-2 text-center text-sm font-semibold text-stitch-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'nav.tryAi', 'Try AI interview')}
                  </Link>
                  <Link
                    to="/become-interviewer"
                    className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'nav.becomeInterviewer', 'Become an Interviewer')}
                  </Link>
                  <Link
                    to="/login"
                    className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'auth.login', 'Login')}
                  </Link>
                  <Link
                    to="/register"
                    className="block rounded-stitch-md bg-stitch-primary px-3 py-2 text-center text-sm font-semibold text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'auth.register', 'Sign Up')}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/about"
                    className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'nav.about', 'About')}
                  </Link>
                  {userRole === UserRole.Interviewer && (
                    <>
                      <Link
                        to={bookingsPath}
                        className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getTranslation(t, 'bookings.interviewerBookings', 'My Bookings')}
                      </Link>
                      <Link
                        to="/time-slots"
                        className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getTranslation(t, 'timeSlots.manage', 'Manage Slots')}
                      </Link>
                    </>
                  )}
                  {userRole !== UserRole.Interviewer && (
                    <>
                      <Link
                        to="/interviewers"
                        className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getTranslation(t, 'interviewers.title', 'Find Interviewers')}
                      </Link>
                      <Link
                        to={bookingsPath}
                        className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getTranslation(t, 'bookings.myBookings', 'My Bookings')}
                      </Link>
                      <Link
                        to="/become-interviewer"
                        className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getTranslation(t, 'nav.becomeInterviewer', 'Become an Interviewer')}
                      </Link>
                    </>
                  )}
                  <Link
                    to="/interview-training"
                    className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'nav.aiTraining', 'AI Training')}
                  </Link>
                  <Link
                    to="/profile"
                    className="block rounded-stitch-md px-3 py-2 text-sm font-medium text-stitch-fg-soft"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {getTranslation(t, 'profile.title', 'Profile')}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full rounded-stitch-md px-3 py-2 text-left text-sm font-medium text-stitch-fg-soft"
                  >
                    {getTranslation(t, 'auth.logout', 'Logout')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="border-t border-stitch-border bg-stitch-footer-bg text-stitch-footer-fg">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mb-3 flex justify-center">
            <span className="text-xl font-bold">InterviewR</span>
          </div>
          <p className="mx-auto mb-2 max-w-lg text-sm text-stitch-footer-muted">
            {getTranslation(
              t,
              'footer.description',
              'Connect with industry experts for better interview preparation.',
            )}
          </p>
          <p className="text-sm text-stitch-footer-muted">
            {getTranslation(t, 'footer.contact', 'Contact us:')}{' '}
            <a
              href="mailto:info@interview-r.com"
              className="text-stitch-footer-fg/90 underline decoration-stitch-footer-muted underline-offset-2 hover:decoration-stitch-primary"
            >
              info@interview-r.com
            </a>
          </p>
          <p className="mt-4 text-xs text-stitch-footer-muted">
            &copy; {new Date().getFullYear()} InterviewR.{' '}
            {getTranslation(t, 'footer.copyright', 'All rights reserved.')}
          </p>
          <p className="mt-1 text-xs text-stitch-footer-muted/80">
            {getTranslation(t, 'footer.entrepreneur', 'Nikoloz Turkishvili, Tbilisi, Georgia')}
          </p>
          <nav className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-stitch-footer-muted">
            <Link to="/privacy-policy" className="transition hover:text-white">
              Privacy Policy
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="transition hover:text-white">
              Terms of Service
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/try-interview" className="transition hover:text-white">
              Free AI demo
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/become-interviewer" className="transition hover:text-white">
              {getTranslation(t, 'nav.becomeInterviewer', 'Become an Interviewer')}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterAdSlot() {
  const { pathname } = useLocation();
  const hideFooterAd =
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/payment') ||
    pathname.startsWith('/profile/setup') ||
    pathname.startsWith('/admin');
  if (!ADS_ENABLED || hideFooterAd) return null;
  return (
    <div className="border-t border-stitch-border bg-stitch-surface-2/60 py-6 dark:bg-stitch-surface-2/30">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <AdBanner slot="footer-banner" />
      </div>
    </div>
  );
}

function Content() {
  return (
    <main className="min-h-0 flex-grow bg-stitch-canvas">
      <Suspense
        fallback={
          <div className="flex h-64 items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </main>
  );
}

export function Root() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <Content />
      <FooterAdSlot />
      <Footer />
      <CookieConsent />
    </div>
  );
}
